import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { ArrowLeft, Send, Plus, Image as ImageIcon, Camera, FileText, Mic, StopCircle, X, Loader2, Play, Film, Download, Music } from "lucide-react";
import api from "@/lib/api";
import { uploadFileChunked, fileUrl } from "@/lib/uploads";
import { compressImage, dataSaverOn, videoPoster } from "@/lib/imageCompress";
import { encodeImageMeta, BlurImage } from "@/lib/blur";
import { AudioPlayer } from "@/components/chat/MediaPlayers";
import MediaViewer from "@/components/chat/MediaViewer";
import { getInitials, avatarColor } from "@/lib/avatar";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

export default function SocialThread({ peer, onBack, socketMessage }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [attachOpen, setAttachOpen] = useState(false);
  const [uploadPct, setUploadPct] = useState(null);
  const [recording, setRecording] = useState(false);
  const [viewer, setViewer] = useState(null);
  const endRef = useRef(null);
  const imageInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const recorderRef = useRef(null);
  const composerRef = useRef(null);

  const load = () => api.get(`/social/inbox/${peer.id}`).then(({ data }) => setMessages(data.messages)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [peer.id]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  useEffect(() => {
    if (!attachOpen) return;
    const onDoc = (e) => { if (composerRef.current && !composerRef.current.contains(e.target)) setAttachOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [attachOpen]);

  // live incoming social DMs for this peer
  useEffect(() => {
    if (!socketMessage) return;
    const m = socketMessage;
    if ((m.from === peer.id && m.to === user.id) || (m.from === user.id && m.to === peer.id)) {
      setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
    }
  }, [socketMessage, peer.id, user.id]);

  const post = async (payload) => {
    try { const { data } = await api.post(`/social/inbox/${peer.id}`, payload); setMessages((prev) => (prev.some((x) => x.id === data.id) ? prev : [...prev, data])); }
    catch { toast.error("Send failed"); }
  };

  const send = async () => {
    const val = text.trim();
    if (!val) return;
    setText("");
    post({ type: "text", text: val });
  };

  const attach = async (e, kind) => {
    let file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024 * 1024) { toast.error("Max 500MB"); e.target.value = ""; return; }
    setUploadPct(0);
    try {
      if (kind === "image") {
        if (dataSaverOn()) file = await compressImage(file);
        const meta = await encodeImageMeta(file);
        const { url } = await uploadFileChunked(file, setUploadPct, "social");
        await post({ type: "image", media: url, media_name: file.name, ...meta });
      } else if (kind === "video") {
        let extra = {};
        const poster = await videoPoster(file);
        if (poster) { const meta = await encodeImageMeta(poster); const { url: pu } = await uploadFileChunked(poster, null, "social"); extra = { poster: pu, ...meta }; }
        const { url } = await uploadFileChunked(file, setUploadPct, "social");
        await post({ type: "video", media: url, media_name: file.name, ...extra });
      } else {
        const { url } = await uploadFileChunked(file, setUploadPct, "social");
        await post({ type: "document", media: url, media_name: file.name });
      }
    } catch { toast.error("Upload failed"); }
    finally { setUploadPct(null); e.target.value = ""; }
  };

  const toggleRecord = async () => {
    if (recording) { recorderRef.current?.stop(); setRecording(false); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 } });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : (MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "");
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime, audioBitsPerSecond: 24000 } : { audioBitsPerSecond: 24000 });
      const chunks = [];
      rec.ondataavailable = (ev) => chunks.push(ev.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((tr) => tr.stop());
        const blob = new Blob(chunks, { type: "audio/webm" });
        const f = new File([blob], "voice.webm", { type: "audio/webm" });
        setUploadPct(0);
        try { const { url } = await uploadFileChunked(f, setUploadPct, "social"); await post({ type: "audio", media: url, media_name: "voice.webm" }); }
        catch { toast.error("Upload failed"); } finally { setUploadPct(null); }
      };
      recorderRef.current = rec; rec.start(); setRecording(true);
    } catch { toast.error("Microphone access denied"); }
  };

  const renderBody = (m) => {
    if (m.type === "image") return (
      <button onClick={() => setViewer({ items: [{ url: fileUrl(m.media), type: "image", name: m.media_name }], idx: 0 })} data-testid={`social-img-${m.id}`} className="block">
        <BlurImage src={fileUrl(m.media)} hash={m.blur_hash} className="inline-block rounded-xl max-h-64" imgClassName="block rounded-xl max-h-64 object-cover" />
      </button>
    );
    if (m.type === "video") return (
      <button onClick={() => setViewer({ items: [{ url: fileUrl(m.media), type: "video", name: m.media_name }], idx: 0 })} data-testid={`social-video-${m.id}`} className="relative block rounded-xl overflow-hidden max-h-64">
        {m.poster ? <img src={fileUrl(m.poster)} alt="" className="max-h-64 object-cover" /> : <div className="h-40 w-56 grid place-items-center bg-black/40"><Film size={30} className="text-[var(--rc-muted)]" /></div>}
        <span className="absolute inset-0 grid place-items-center"><span className="h-12 w-12 grid place-items-center rounded-full bg-black/55"><Play size={24} className="text-white ml-0.5" fill="white" /></span></span>
      </button>
    );
    if (m.type === "audio") return <div data-testid={`social-audio-${m.id}`}><AudioPlayer src={fileUrl(m.media)} mine={m.from === user.id} /></div>;
    if (m.type === "document") return (
      <div className="flex items-center gap-2.5 min-w-[180px]" data-testid={`social-doc-${m.id}`}>
        <span className="h-10 w-10 grid place-items-center rounded-lg bg-[var(--rc-gold)]/15 text-[var(--rc-gold)] shrink-0"><FileText size={20} /></span>
        <div className="min-w-0 flex-1">
          <div className="text-sm truncate">{m.media_name || t("s_document")}</div>
          <a href={fileUrl(m.media)} target="_blank" rel="noreferrer" download className="text-xs text-[var(--rc-gold)] font-medium inline-flex items-center gap-1"><Download size={13} /> {t("download")}</a>
        </div>
      </div>
    );
    return <div className="text-sm [overflow-wrap:anywhere] whitespace-pre-wrap">{m.text}</div>;
  };

  const tiles = [
    { id: "photos", icon: ImageIcon, label: t("attach_photos"), color: "#7C3AED", action: () => imageInputRef.current?.click() },
    { id: "camera", icon: Camera, label: t("attach_camera"), color: "#059669", action: () => cameraInputRef.current?.click() },
    { id: "document", icon: FileText, label: t("attach_document"), color: "#2563EB", action: () => fileInputRef.current?.click() },
  ];

  return (
    <div className="flex flex-col h-full" data-testid="social-thread">
      <div className="flex items-center gap-3 pb-3 border-b border-[var(--rc-border)]">
        <button onClick={onBack} data-testid="social-thread-back" className="text-[var(--rc-silver)]"><ArrowLeft size={22} /></button>
        <div className="h-10 w-10 rounded-2xl grid place-items-center text-[var(--rc-gold)] font-semibold border-2 border-[var(--rc-gold)] overflow-hidden" style={{ backgroundColor: peer.avatar ? "var(--rc-bg)" : avatarColor(peer.name) }}>
          {peer.avatar ? <img src={peer.avatar} alt="" className="h-full w-full object-cover" /> : getInitials(peer.name)}
        </div>
        <div className="font-medium truncate">{peer.name}</div>
      </div>

      <div className="flex-1 overflow-y-auto no-scrollbar py-4 space-y-2">
        {messages.map((m) => {
          const mine = m.from === user.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`social-msg-${m.id}`}>
              <div className={`max-w-[78%] rounded-2xl px-2.5 py-2 ${mine ? "bg-[var(--rc-bubble-out)]" : "bg-[var(--rc-surface)]"}`}>
                {renderBody(m)}
                <div className="text-[10px] text-[var(--rc-muted)] mt-0.5 text-right">{dayjs(m.created_at).format("HH:mm")}</div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {uploadPct !== null && (
        <div className="flex items-center gap-3 text-sm py-2" data-testid="social-upload-progress">
          <Loader2 size={16} className="animate-spin text-[var(--rc-gold)]" />
          <div className="flex-1 h-1.5 rounded-full bg-[var(--rc-surface)] overflow-hidden"><div className="h-full bg-[var(--rc-gold)] transition-all" style={{ width: `${uploadPct}%` }} /></div>
          <span className="text-[var(--rc-muted)]">{uploadPct}%</span>
        </div>
      )}

      <div ref={composerRef} className="relative flex items-center gap-2 pt-2 border-t border-[var(--rc-border)]">
        <input ref={imageInputRef} type="file" accept="image/*" hidden onChange={(e) => attach(e, "image")} data-testid="social-image-input" />
        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => attach(e, "image")} data-testid="social-camera-input" />
        <input ref={fileInputRef} type="file" hidden onChange={(e) => attach(e, e.target.files[0]?.type.startsWith("video") ? "video" : "document")} data-testid="social-doc-input" />

        {attachOpen && (
          <div className="absolute z-20 bottom-full left-0 mb-2 rounded-2xl border border-[var(--rc-border)] bg-[var(--rc-surface)] shadow-2xl p-3 rc-pop" data-testid="social-attach-menu">
            <div className="grid grid-cols-3 gap-3 w-[228px]">
              {tiles.map((o) => (
                <button key={o.id} data-testid={`social-attach-${o.id}`} onClick={() => { setAttachOpen(false); o.action(); }} className="flex flex-col items-center gap-1.5 group">
                  <span className="h-14 w-14 grid place-items-center rounded-full text-white group-hover:scale-105 transition-transform" style={{ backgroundColor: o.color }}><o.icon size={22} /></span>
                  <span className="text-[11px] text-[var(--rc-muted)]">{o.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <button onClick={() => setAttachOpen((v) => !v)} data-testid="social-attach-btn"
          className={`h-10 w-10 shrink-0 grid place-items-center rounded-full transition-all ${attachOpen ? "text-[var(--rc-gold)] bg-[var(--rc-gold)]/10 rotate-45" : "text-[var(--rc-silver)] hover:text-[var(--rc-gold)]"}`}><Plus size={24} /></button>
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }}
          placeholder={t("s_message_ph")} data-testid="social-thread-input"
          className="flex-1 min-w-0 rounded-full bg-[var(--rc-surface)] border border-[var(--rc-border)] px-4 py-2.5 focus:outline-none focus:border-[var(--rc-gold)]/50" />
        {text.trim() ? (
          <button onClick={send} data-testid="social-thread-send" className="h-11 w-11 grid place-items-center rounded-full bg-[var(--rc-gold)] text-black shrink-0"><Send size={18} /></button>
        ) : (
          <button onClick={toggleRecord} data-testid="social-record-btn" className={`h-11 w-11 grid place-items-center rounded-full shrink-0 transition-colors ${recording ? "bg-red-500 text-white rc-ring" : "bg-[var(--rc-gold)] text-black"}`}>
            {recording ? <StopCircle size={18} /> : <Mic size={18} />}
          </button>
        )}
      </div>

      {viewer && <MediaViewer items={viewer.items} startIndex={viewer.idx} onClose={() => setViewer(null)} />}
    </div>
  );
}
